import type { Dayjs } from 'dayjs';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';

import { Iconify } from '../../../../iconify';

// ----------------------------------------------------------------------

type HeadFilterDateTimeFieldProps = {
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  setFromDate: (date: Dayjs | null) => void;
  setToDate: (date: Dayjs | null) => void;
  t: (key: string) => string;
};

// ----------------------------------------------------------------------

export function HeadFilterDateTimeField({
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  t,
}: HeadFilterDateTimeFieldProps) {
  const handleFromDateChange = (val: Dayjs | null) => {
    if (!val) {
      setFromDate(null);
      return;
    }
    setFromDate(val);

    // If toDate exists and is before the new fromDate, update toDate to match fromDate
    if (toDate && toDate.isBefore(val)) {
      setToDate(val);
    }
  };

  const handleToDateChange = (val: Dayjs | null) => {
    if (!val || !fromDate) {
      setToDate(val);
      return;
    }

    const isSameDay = val.isSame(fromDate, 'day');

    if (isSameDay && val.isBefore(fromDate)) {
      // Set the time of toDate to match fromDate
      const adjusted = val.hour(fromDate.hour()).minute(fromDate.minute());
      setToDate(adjusted);
    } else {
      setToDate(val);
    }
  };

  const datePickerStyles = {
    '.MuiDialogActions-root .MuiButton-root:last-of-type': {
      // color: '#fff !important',
    },
    '.MuiDialogActions-root .MuiButton-root:first-of-type': {
      // color: '#fff !important',
    },
    '.MuiDateTimePickerToolbar-dateContainer .MuiButtonBase-root': {
      background: 'transparent',
      '&:hover': {
        background: 'transparent',
      },
    },
    '.MuiDateTimePickerToolbar-timeContainer .MuiButtonBase-root': {
      background: 'transparent',
      '&:hover': {
        background: 'transparent',
      },
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
      <MobileDateTimePicker
        ampm
        format="DD/MM/YYYY hh:mm A"
        name="fromDate"
        label={t('FROM')}
        value={fromDate}
        onChange={handleFromDateChange}
        localeText={{
          cancelButtonLabel: t('CANCEL'),
          okButtonLabel: t('OK'),
          toolbarTitle: t('SELECT_DATE_TIME'),
        }}
        slotProps={{
          actionBar: {
            actions: ['cancel', 'accept'],
          },
          textField: {
            InputLabelProps: { shrink: true },
            sx: { width: '100%', margin: '2px' },
            InputProps: {
              endAdornment: fromDate && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setFromDate(null);
                      const input = e.currentTarget.closest('div')?.querySelector('input');
                      input?.blur();
                    }}
                    size="small"
                    sx={{ color: 'text.secondary' }}
                  >
                    <Iconify icon="solar:close-circle-bold" width={20} height={20} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          },
          mobilePaper: {
            sx: datePickerStyles,
          },
        }}
      />
      <MobileDateTimePicker
        ampm
        format="DD/MM/YYYY hh:mm A"
        name="toDate"
        label={t('TO')}
        value={toDate}
        onChange={handleToDateChange}
        minDateTime={fromDate ?? undefined}
        localeText={{
          cancelButtonLabel: t('CANCEL'),
          okButtonLabel: t('OK'),
          toolbarTitle: t('SELECT_DATE_TIME'),
        }}
        slotProps={{
          actionBar: {
            actions: ['cancel', 'accept'],
          },
          mobilePaper: {
            sx: datePickerStyles,
          },
          textField: {
            InputLabelProps: { shrink: true },
            sx: { width: '100%', margin: '2px' },
            InputProps: {
              endAdornment: toDate && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setToDate(null);
                      const input = e.currentTarget.closest('div')?.querySelector('input');
                      input?.blur();
                    }}
                    size="small"
                    sx={{ color: 'text.secondary' }}
                  >
                    <Iconify icon="solar:close-circle-bold" width={20} height={20} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          },
        }}
      />
    </Box>
  );
}
